import type { LocationChangeAction } from 'connected-react-router';
import { LOCATION_CHANGE } from 'connected-react-router';
import type { NextRouter } from 'next/router';
import { compose, prop } from 'ramda';
import type { Reducer } from 'redux';
import type { RoutingState } from '@peloton/redux/selectors';
import { getQueryParams, parseSearch } from '@peloton/redux/selectors';
import { isDefined } from '@peloton/types/isDefined';

const initialState = {
  name: undefined as string | undefined,
  source: '',
  // Tracks how the modal was opened so ConnectedModal can choose the right
  // source of truth.  'action' = programmatic openModal() dispatch (e.g. Find
  // Members button), 'url' = LOCATION_CHANGE read the name from ?modal= query
  // param.  On locale-prefixed pages connected-react-router only fires
  // LOCATION_CHANGE once (initial load), so 'url'-sourced names can become
  // stale — ConnectedModal falls back to router.query.modal for those.
  openedVia: 'none' as 'none' | 'action' | 'url',
};

export const modalReducer: Reducer<typeof initialState> = (
  state = initialState,
  action: ModalAction | LocationChangeAction,
) => {
  switch (action.type) {
    case ModalActionType.Open:
      return {
        name: action.payload.name,
        source: action.payload.source,
        openedVia: 'action' as const,
      };
    case LOCATION_CHANGE: {
      const modalName = parseSearch(action.payload.location).modal;
      if (isDefined(modalName)) {
        return {
          ...state,
          name: modalName,
          openedVia: 'url' as const,
        };
      } else {
        return initialState;
      }
    }
    case ModalActionType.Close:
      return initialState;
    default:
      return state;
  }
};

export const getActiveModalName = ({ modal }: ModalSelectorState) => modal.name;

export const getActiveModalSource = ({ modal }: ModalSelectorState) => modal.source;

export const getActiveModalOpenedVia = ({ modal }: ModalSelectorState) => modal.openedVia;

export const getModalNameFromLocation = compose<
  RoutingState,
  Record<string, string>,
  string | undefined
>(prop('modal'), getQueryParams);

export const openModal = (name: ModalName, source: string = '') => ({
  type: ModalActionType.Open,
  payload: { name, source },
});

export const closeModal = (router: NextRouter, additionalParams: string[] = []) => {
  return {
    type: ModalActionType.Close,
    payload: { router, additionalParams },
  };
};

export type ModalAction = {
  type: ModalActionType;
  payload: {
    name: ModalName;
    source: string;
  };
};

export type ModalSelectorState = {
  modal: typeof initialState;
};

export enum ModalActionType {
  Open = 'pelo/modal/open',
  Close = 'pelo/modal/close',
}

export type ModalName = string;
